import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { bankFeedApi } from '@/domain/bankFeed/api';
import { queryOptions, useApiMutation } from '@/services/query';
import { showApiErrorToast } from '@/services/api';
import { yieldSuccessNotification } from '@/lib/toasts';
import { bankFeedKeys } from '@/domain/bankFeed/queries/keys';
import type { ConnectionsCache } from '@/domain/bankFeed/queries/connections';

// Always-fresh: cacheTime: 0 evicts the cache as soon as the editor closes, so the
// next open refetches the company's ERP accounts. The AG Grid cell editor is exclusive
// (only one open at a time), so there's no concurrent subscriber to benefit from a
// shared cache anyway.
export const bankFeedErpAccountsQuery = (companyId: string) =>
  queryOptions({
    queryKey: bankFeedKeys.erpAccounts(companyId),
    queryFn: () => bankFeedApi.getErpAccounts(),
    cacheTime: 0,
  });

// A failed load must not end as an empty dropdown with no reason given: the backend explains
// it (e.g. Priority not connected, API forms not enabled), so its message is shown. Each
// editor open is a fresh fetch; the toast's message-keyed id keeps a repeat failure from
// stacking a second toast while the first is still on screen.
export const useBankFeedErpAccounts = (companyId: string) => {
  const query = useQuery(bankFeedErpAccountsQuery(companyId));
  const { error } = query;

  useEffect(() => {
    if (error) showApiErrorToast(error);
  }, [error]);

  return query;
};

// Cross-cache write: matchErpAccount returns the updated inner BankFeedConnectionAccount,
// which lives at connections[i].accounts[j]. Immer produce handles the deep find-and-replace
// without nested spread/map chains. The mutation lives here (ERP feature owner) rather
// than connections.ts because consumers reach for it as part of the ERP-account UX.
export const useMatchErpAccountMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: bankFeedApi.matchErpAccount,
    onSuccess: (updated) => {
      qc.setQueryData<ConnectionsCache>(
        bankFeedKeys.connections(companyId),
        produce((draft) => {
          if (!draft) return;
          const conn = draft.items.find((c) => c.id === updated.connection_id);
          if (!conn) return;
          // Row identity is `uid`, not `id`: a multi-currency bank account contributes one row
          // per currency and they all share the same `id`. Matching by `id` replaces every
          // currency row with this one result — other currencies' bindings lost, uid duplicated.
          conn.accounts = conn.accounts.map((a) => (a.uid === updated.uid ? updated : a));
        }),
      );
      yieldSuccessNotification(
        updated.erp_account
          ? {
              id: 'configurations.company.bankFeed.tabs.connections.toast.erpAccount.match.success',
              defaultMessage: 'Erp account matched successfully!',
            }
          : {
              id: 'configurations.company.bankFeed.tabs.connections.toast.erpAccount.reset.success',
              defaultMessage: 'Erp account has been reset',
            },
      );
    },
    onError: (error) => showApiErrorToast(error),
  });
};
